import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CaretDown, CaretRight, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

function TemplateBox({ label, hint, value, onSave, disabled, testid }) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold">{label}</span>
        <span className="text-[11px] text-muted-foreground">{hint}</span>
      </div>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} disabled={disabled} rows={5} maxLength={5000} className="rounded-sm text-xs" data-testid={`${testid}-text`} />
      {!disabled && <Button type="button" size="sm" variant="outline" className="h-7 rounded-sm" disabled={text === value} onClick={() => onSave(text)} data-testid={`${testid}-save`}>Save {label.toLowerCase()}</Button>}
    </div>
  );
}

function TemplateEditor({ templateBase, recipientsKey }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["email-template", templateBase], queryFn: async () => (await api.get(templateBase)).data });
  const save = useMutation({
    mutationFn: async ({ scope, text }) => (await api.put(`${templateBase}/${scope}`, { text })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-template", templateBase] });
      qc.invalidateQueries({ queryKey: recipientsKey });
      toast.success("Template saved");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  if (!data) return null;
  return (
    <div className="border border-border bg-muted/30 p-3 space-y-3" data-testid="po-template-editor">
      <p className="text-[11px] text-muted-foreground">
        Your template is used if you have one, otherwise the company template. Fill-in tags:{" "}
        {data.tags.map((t) => <code key={t} className="mx-0.5 px-1 bg-background border border-border text-[10px]">{`{${t}}`}</code>)}
      </p>
      <TemplateBox label="My template" hint="Only you" value={data.mine} onSave={(text) => save.mutate({ scope: "me", text })} testid="po-template-mine" />
      <TemplateBox label="Company template" hint={data.can_edit_company ? "Everyone in the company" : "Set by administrators"} value={data.company} disabled={!data.can_edit_company} onSave={(text) => save.mutate({ scope: "company", text })} testid="po-template-company" />
    </div>
  );
}

// Editable email message for the Send pop-ups, pre-filled from the saved template.
export function EmailMessageBox({ templateBase, recipientsKey, message, onChange, templateMessage, source, linesNote = "The document lines, totals and PDF are always included.", tagHint = "{po_number}" }) {
  const [showTemplates, setShowTemplates] = useState(false);
  return (
    <div className="space-y-2" data-testid="po-email-message">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Message</span>
        {templateMessage && message !== templateMessage && (
          <button type="button" onClick={() => onChange(templateMessage)} className="text-[11px] text-primary hover:underline flex items-center gap-1" data-testid="po-email-reset">
            <ArrowCounterClockwise size={12} /> Reset to template
          </button>
        )}
      </div>
      <Textarea value={message} onChange={(e) => onChange(e.target.value)} rows={7} maxLength={10000} className="rounded-sm text-sm"
        placeholder={`Leave blank to use the standard message, or type your own. Tags like ${tagHint} are filled in when sent.`} data-testid="po-email-message-text" />
      <p className="text-[11px] text-muted-foreground" data-testid="po-email-template-source">
        {source === "mine" ? "Pre-filled from your template." : source === "company" ? "Pre-filled from the company template." : "No template saved yet."} Changes here apply to this email only. {linesNote}
      </p>
      <button type="button" onClick={() => setShowTemplates((v) => !v)} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="po-email-templates-toggle">
        {showTemplates ? <CaretDown size={12} /> : <CaretRight size={12} />} Manage saved templates
      </button>
      {showTemplates && <TemplateEditor templateBase={templateBase} recipientsKey={recipientsKey} />}
    </div>
  );
}

export const POEmailMessage = ({ orderId, ...rest }) => (
  <EmailMessageBox templateBase="/po-email-template" recipientsKey={["po-email-recipients", orderId]} linesNote="The PO lines, totals and PDF are always included." {...rest} />
);
